import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import CommentsSection from "./CommentsSection";
import {
  ArrowBigUp, MessageSquare, Share2, BadgeCheck, Globe,
  Anchor, Ship, Wallet, MapPin, Check, Route, Navigation, ShieldCheck,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "k" : "" + n);

const JD = ({ icon: Icon, l, v }) => (
  <div className="flex items-start gap-2">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{l}</div>
      <div className="truncate text-sm font-semibold text-gray-800">{v}</div>
    </div>
  </div>
);

export const PostCard = ({ post: p, score, upvoted, onVote, onApply, applied }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(p.comments);
  const authorAvatar = p.author_avatar_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.author_avatar_path}` : p.author_picture || null;
  const soon = (f) => toast({ title: `${f} coming soon`, description: "This is part of the next release." });

  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`post-${p.id}`}>
      <div className="flex items-center gap-3">
        {authorAvatar ? (
          <img src={authorAvatar} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" data-testid={`post-author-avatar-${p.id}`} />
        ) : (
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${p.isCompany ? "bg-slate-100 text-slate-700" : "bg-orange-50 text-[#FF4500]"}`} data-testid={`post-author-avatar-${p.id}`}>
            {p.author.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-sm font-bold text-gray-900">
            {p.author}
            {p.isCompany && <BadgeCheck className="h-3.5 w-3.5 text-[#002855]" />}
            {p.author_complete && (
              <span className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700"
                title="Photo, rank, sea service and certificates all added" data-testid={`post-complete-badge-${p.id}`}>
                <ShieldCheck className="h-3 w-3" /> Complete profile
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <span>{p.time === "now" || p.time === "just now" ? "just now" : p.time + " ago"}</span>
            <span>·</span>
            <button onClick={() => navigate(`/c/${encodeURIComponent(p.community)}`)} data-testid={`post-community-${p.id}`}
              className="flex items-center gap-1 font-semibold text-slate-500 hover:text-[#002855] hover:underline">
              <Globe className="h-3 w-3" /> {p.community}
            </button>
          </div>
        </div>
      </div>

      <h3 className="mt-3 text-lg font-bold leading-snug text-gray-900">{p.title}</h3>
      {p.body && <p className="mt-2 text-sm leading-relaxed text-gray-600">{p.body}</p>}

      {(p.image_path || p.image_preview) && (
        <img src={p.image_preview || `${API_BASE}/files/${p.image_path}`} alt="" data-testid={`post-image-${p.id}`}
          className="mt-3 max-h-[480px] min-h-[120px] w-full rounded-xl bg-gray-100 object-contain" />
      )}

      {p.voyage && (p.voyage.vessel || p.voyage.route || p.voyage.rank) && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid={`post-voyage-${p.id}`}>
          <div className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500"><Navigation className="h-3.5 w-3.5" /> Voyage Card</div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {p.voyage.vessel && <JD icon={Ship} l="Vessel" v={p.voyage.vessel} />}
            {p.voyage.vessel_type && <JD icon={Anchor} l="Type" v={p.voyage.vessel_type} />}
            {p.voyage.rank && <JD icon={BadgeCheck} l="Rank" v={p.voyage.rank} />}
            {p.voyage.route && <JD icon={Route} l="Route" v={p.voyage.route} />}
          </div>
        </div>
      )}

      {p.type === "job" && p.job && (
        <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl bg-gray-50 p-3 sm:grid-cols-4">
          <JD icon={Anchor} l="Rank" v={p.job.rank} />
          <JD icon={Ship} l="Vessel" v={p.job.vessel} />
          <JD icon={Wallet} l="Wages" v={p.job.salary} />
          <JD icon={MapPin} l="Location" v={p.job.location} />
        </div>
      )}

      {p.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {p.tags.map((t) => <span key={t} className="text-sm font-medium text-slate-500 hover:text-[#002855]">#{t.replace(/\s/g, "")}</span>)}
        </div>
      )}

      <div className="mt-3 flex items-center gap-1 border-t border-gray-100 pt-3 text-gray-500">
        <button onClick={() => onVote(p.id)} data-testid={`upvote-${p.id}`} className={`rounded p-1 hover:bg-gray-100 ${upvoted ? "text-[#FF4500]" : ""}`}>
          <ArrowBigUp className="h-5 w-5" fill={upvoted ? "#FF4500" : "none"} />
        </button>
        <span className="text-sm font-bold text-gray-700" data-testid={`score-${p.id}`}>{fmt(score)}</span>
        <button onClick={() => setShowComments((s) => !s)} data-testid={`comments-toggle-${p.id}`} className={`ml-2 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-gray-100 ${showComments ? "text-[#002855]" : ""}`}><MessageSquare className="h-4 w-4" /> {commentCount}</button>
        <button onClick={() => soon("Share")} className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-gray-100"><Share2 className="h-4 w-4" /> Share</button>
        {p.type === "job" && (
          applied ? (
            <Button disabled data-testid={`applied-${p.id}`} className="ml-auto h-8 rounded-full bg-emerald-600 px-4 text-xs font-bold text-white">
              <Check className="mr-1 h-3.5 w-3.5" /> Applied
            </Button>
          ) : (
            <Button onClick={() => onApply(p)} data-testid={`apply-${p.id}`} className="ml-auto h-8 rounded-full bg-[#FF4500] px-4 text-xs font-bold text-white hover:bg-[#e03d00]">Apply</Button>
          )
        )}
      </div>

      {showComments && <CommentsSection postId={p.id} onCountChange={setCommentCount} />}
    </article>
  );
};

export default PostCard;
